import {
  Container,
  Stack,
  Group,
  Button,
  Title,
  Text,
  Grid,
  Paper,
  Badge,
  Loader,
  TextInput,
  ScrollArea,
  Table,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconSearch,
  IconFileTypeCsv,
  IconFileTypePdf,
} from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { usePerformRequest } from "../../../../lib/usePerformRequest";
import { Endpoints } from "../../../../lib/endpoints";
import { useToastNotification } from "../../../../hooks/useToastNotification";
import { STORAGE_TOKEN_KEY } from "../../../../contexts/AuthContext";
import { HttpMethodProps } from "../../../../types";

interface LoanScheduleDetailLoan {
  loanId: string;
  borrowerName: string;
  borrowerEmail?: string;
  borrowerPhone?: string;
  loanProduct: string;
  principal: number;
  interest: number;
  fees: number;
  totalDue: number;
  amountPaid: number;
  remainingBalance: number;
  repaymentModel: string;
  repaymentFrequency: string;
  duration: number;
  approvalType?: string;
  approvedAt?: number;
  disbursedAt?: number;
  approvedBy?: string;
}

interface LoanScheduleDetailEntry {
  paymentNumber: number;
  dueDate: number;
  principalPortion: number;
  interestPortion: number;
  feePortion: number;
  totalDue: number;
  paidAmount: number;
  remainingBalance: number;
  status: "paid" | "overdue" | "upcoming" | "pending";
  paidDate?: number;
  daysOverdue?: number;
  daysUntilDue?: number | null;
}

interface LoanScheduleDetailResponse {
  loan: LoanScheduleDetailLoan;
  schedule: LoanScheduleDetailEntry[];
}

export default function LoanScheduleDetailPage() {
  const { loanID } = useParams<{ loanID: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToastNotification();
  const [scheduleFilter, setScheduleFilter] = useState("");
  const [exportingFormat, setExportingFormat] = useState<"csv" | "pdf" | null>(null);

  const detailEndpoint = loanID ? Endpoints.GetLoanScheduleDetail(loanID) : null;
  const detailMethod = (detailEndpoint?.method ?? "GET") as HttpMethodProps;
  const detailRoute = detailEndpoint?.route ?? "";
  const {
    data: detailResponse,
    isLoading,
  } = usePerformRequest<{ data: LoanScheduleDetailResponse }>({
    method: detailMethod,
    route: detailRoute,
  });

  const loanDetail = detailResponse?.data?.loan;
  const schedule = detailResponse?.data?.schedule || [];

  const filteredSchedule = useMemo(() => {
    if (!scheduleFilter.trim()) return schedule;
    const term = scheduleFilter.toLowerCase();
    return schedule.filter((entry) => {
      const dateString = new Date(entry.dueDate).toLocaleString("en-NG");
      return `${entry.paymentNumber} ${dateString} ${entry.status}`.toLowerCase().includes(term);
    });
  }, [schedule, scheduleFilter]);

  const formatCurrency = (amount: number) =>
    `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: LoanScheduleDetailEntry["status"]) => {
    switch (status) {
      case "paid":
        return "green";
      case "overdue":
        return "red";
      case "upcoming":
        return "blue";
      default:
        return "gray";
    }
  };

  const handleExport = async (format: "csv" | "pdf") => {
    if (!loanID) return;
    try {
      setExportingFormat(format);
      const endpoint = Endpoints.ExportLoanSchedule(loanID, format);
      const token = typeof window !== "undefined" ? localStorage.getItem(STORAGE_TOKEN_KEY) : null;
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(endpoint.route, {
        method: endpoint.method,
        headers,
      });

      if (!response.ok) throw new Error("Failed to export loan schedule");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `loan-schedule-${loanID}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      success(`Loan schedule exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      showError(error?.message || "Failed to export loan schedule");
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between">
          <Group gap="sm">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>
              Back to schedules
            </Button>
            <div>
              <Title order={2} fw={700}>
                Loan Schedule Report
              </Title>
              <Text size="sm" c="dimmed">
                Detailed amortization schedule for this loan
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconFileTypeCsv size={16} />}
              loading={exportingFormat === "csv"}
              onClick={() => handleExport("csv")}
              disabled={!loanID}
            >
              Export CSV
            </Button>
            <Button
              variant="light"
              leftSection={<IconFileTypePdf size={16} />}
              loading={exportingFormat === "pdf"}
              onClick={() => handleExport("pdf")}
              disabled={!loanID}
            >
              Export PDF
            </Button>
          </Group>
        </Group>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : loanDetail ? (
          <Stack gap="lg">
            <Paper shadow="sm" p="lg" radius="md" withBorder>
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Approved By
                  </Text>
                  <Text fw={600}>{loanDetail.approvedBy || "N/A"}</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Type of Approval
                  </Text>
                  <Text fw={600}>{loanDetail.approvalType?.toUpperCase() || "MANUAL"}</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Approved On
                  </Text>
                  <Text fw={600}>{formatDateTime(loanDetail.approvedAt)}</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Disbursed On
                  </Text>
                  <Text fw={600}>{formatDateTime(loanDetail.disbursedAt)}</Text>
                </Grid.Col>
              </Grid>
            </Paper>

            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                <Paper p="lg" radius="md" withBorder>
                  <Text size="xs" c="dimmed">
                    Borrower
                  </Text>
                  <Text fw={700}>{loanDetail.borrowerName || "N/A"}</Text>
                  <Text size="xs" c="dimmed">
                    {loanDetail.borrowerPhone || loanDetail.borrowerEmail || "—"}
                  </Text>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                <Paper p="lg" radius="md" withBorder>
                  <Text size="xs" c="dimmed">
                    Loan Product
                  </Text>
                  <Text fw={700}>{loanDetail.loanProduct}</Text>
                  <Badge variant="light" mt="xs">
                    {loanDetail.repaymentModel.replace(/_/g, " ").toUpperCase()}
                  </Badge>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                <Paper p="lg" radius="md" withBorder>
                  <Text size="xs" c="dimmed">
                    Repayment Frequency
                  </Text>
                  <Text fw={700}>{loanDetail.repaymentFrequency.toUpperCase()}</Text>
                  <Text size="xs" c="dimmed">
                    Duration: {loanDetail.duration} period(s)
                  </Text>
                </Paper>
              </Grid.Col>
            </Grid>

            <Grid gutter="md">
              {[
                { label: "Principal", value: loanDetail.principal },
                { label: "Interest", value: loanDetail.interest },
                { label: "Fees + Penalty", value: loanDetail.fees },
                { label: "Total Due", value: loanDetail.totalDue },
                { label: "Paid", value: loanDetail.amountPaid },
                { label: "Outstanding", value: loanDetail.remainingBalance },
              ].map((item) => (
                <Grid.Col key={item.label} span={{ base: 12, sm: 6, md: 4 }}>
                  <Paper p="lg" radius="md" withBorder>
                    <Text size="xs" c="dimmed">
                      {item.label}
                    </Text>
                    <Text fw={700} size="lg">
                      {formatCurrency(item.value)}
                    </Text>
                  </Paper>
                </Grid.Col>
              ))}
            </Grid>

            <Paper shadow="sm" p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <TextInput
                    placeholder="Search installments"
                    leftSection={<IconSearch size={16} />}
                    value={scheduleFilter}
                    onChange={(event) => setScheduleFilter(event.currentTarget.value)}
                    style={{ maxWidth: 280 }}
                  />
                </Group>
                <ScrollArea>
                  <Table.ScrollContainer minWidth={1000}>
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Date</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Principal (NGN)</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Interest (NGN)</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Fees + Penalty (NGN)</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Total (NGN)</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Paid (NGN)</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Due (NGN)</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Remaining (NGN)</Table.Th>
                          <Table.Th>Paid Date</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {filteredSchedule.length > 0 ? (
                          filteredSchedule.map((entry) => {
                            const dueAmount = Math.max(0, entry.totalDue - entry.paidAmount);
                            return (
                              <Table.Tr key={entry.paymentNumber}>
                                <Table.Td>
                                  <Group gap={6} align="center">
                                    <ThemeIcon size={10} radius="xl" color={getStatusColor(entry.status)} />
                                    <Text size="sm" tt="capitalize">
                                      {entry.status}
                                    </Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  <Stack gap={0}>
                                    <Text fw={500}>{formatDateTime(entry.dueDate)}</Text>
                                    <Text size="xs" c="dimmed">
                                      Installment #{entry.paymentNumber}
                                    </Text>
                                  </Stack>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600}>{formatCurrency(entry.principalPortion)}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600}>{formatCurrency(entry.interestPortion)}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600}>{formatCurrency(entry.feePortion)}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600}>{formatCurrency(entry.totalDue)}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600} c={entry.paidAmount > 0 ? "green" : "gray"}>
                                    {formatCurrency(entry.paidAmount)}
                                  </Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600} c={dueAmount > 0 ? "orange" : "green"}>
                                    {formatCurrency(dueAmount)}
                                  </Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600}>{formatCurrency(entry.remainingBalance)}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{entry.paidDate ? formatDateTime(entry.paidDate) : "—"}</Text>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })
                        ) : (
                          <Table.Tr>
                            <Table.Td colSpan={10}>
                              <Stack gap={4} align="center" py="md">
                                <Text size="sm" c="dimmed">
                                  No schedule entries match your search.
                                </Text>
                              </Stack>
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </ScrollArea>
              </Stack>
            </Paper>
          </Stack>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Stack gap="sm" align="center">
              <Text fw={600}>Loan schedule not found</Text>
              <Text size="sm" c="dimmed" ta="center">
                We couldn&apos;t find a loan schedule for the provided ID. Please go back and try again.
              </Text>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

